import { useAuth } from 'contexts/auth';
import APILaunch from 'pages/APILaunch';
import AssignmentEditorPage from 'pages/AssignmentEditor';
import AssignmentsPage from 'pages/Assignments';
import ComponentDemoPage from 'pages/ComponentDemo';
import CourseDashboardPage from 'pages/CourseDashboard';
import CourseSetUpPage from 'pages/CourseSetup';
import CreateFormativePage from 'pages/CreateFormative';
import CreateQuizPage from 'pages/CreateQuiz';
import CreateRubricPage from 'pages/CreateRubric';
import EditQuizPage from 'pages/EditQuiz';
import EditRubricPage from 'pages/EditRubric';
import Error404 from 'pages/Error/404';
import AccessDenied from 'pages/Error/AccessDenied';
import SessionExpiredPage from 'pages/Error/SessionExpired';
import GradingPage from 'pages/Grading';
import DeepLinkPickerPage from 'pages/LTI/DeepLinkPicker';
import LTILaunchPage from 'pages/LTI/Launch';
import People from 'pages/People';
import PreviewPage from 'pages/Preview';
import ProfileSetupPage from 'pages/ProfileSetup';
import QuizzesPage from 'pages/Quizzes';
import RubricsPage from 'pages/Rubrics';
import RubricsGetStartedPage from 'pages/RubricsGetStarted';
import SettingsPage from 'pages/Settings';
import SubmissionsPage from 'pages/Submissions';
import ViewAssignmentPage from 'pages/ViewAssignment';
import ViewQuizPage from 'pages/ViewQuiz';
import ViewRubricPage from 'pages/ViewRubric';
import ViewSubmissionPage from 'pages/ViewSubmission';
import { Route, Switch } from 'react-router-dom';

const GrantPermission = ({ exact, path, component, roles, auth, user, userRole }) => {
    const exceptions = [
        '/api-launch',
        '/components',
        '/lti-admin',
        '/lti-incomplete',
        '/lti-launch',
        '/lti/deep-link-picker',
        '/session-expired',
    ];

    if (exceptions.includes(path)) {
        return <Route exact={exact} path={path} component={component} />;
    }

    if (auth && auth.role !== 'admin' && !auth.owner_account_id && user && !user.profile_set_up) {
        return <Route exact={exact} path={path} component={ProfileSetupPage} />;
    } else if (roles.length === 0 || roles?.includes(userRole)) {
        return <Route exact={exact} path={path} component={component} />;
    } else {
        return <Route exact={exact} path={path} component={Error404} />;
    }
};

const APIRoleBasedRouting = () => {
    const { auth, currentUser } = useAuth();

    const getDefaultComponent = (auth) => {
        if (!auth) return AccessDenied;
        return CourseDashboardPage;
    };

    const noAuthRoutes = [
        { path: '/', component: AccessDenied, roles: [], exact: true },
        { path: '/api-launch', component: APILaunch, roles: [], exact: true },
        { path: '/components', component: ComponentDemoPage, roles: [], exact: true },
        { path: '/lti/deep-link-picker', component: DeepLinkPickerPage, roles: [], exact: true },
        { path: '/lti-incomplete', component: AccessDenied, roles: [], exact: true },
        { path: '/session-expired', component: SessionExpiredPage, roles: [], exact: true },
        { path: '/lti-launch', component: LTILaunchPage, roles: [], exact: true },
    ];

    const routes = [
        { path: '/', component: getDefaultComponent(auth), roles: [], exact: true },

        { path: '/api-launch', component: APILaunch, roles: [], exact: true },

        { path: '/assignments', component: AssignmentsPage, roles: ['instructor', 'ta', 'student'], exact: true },
        {
            path: ['/assignments/new', '/assignments/:id/edit'],
            component: AssignmentEditorPage,
            roles: ['instructor', 'ta'],
            exact: true,
        },
        {
            path: '/assignments/:id',
            component: ViewAssignmentPage,
            roles: ['instructor', 'ta', 'student'],
            exact: true,
        },
        { path: '/assignments/:id/grading', component: GradingPage, roles: ['instructor', 'ta'], exact: true },
        {
            path: '/assignments/:id/grading/:submissionId',
            component: GradingPage,
            roles: ['instructor', 'ta'],
            exact: true,
        },
        { path: '/assignments/:id/preview', component: PreviewPage, roles: ['instructor', 'ta'], exact: true },
        {
            path: '/assignments/:id/preview/:submissionId',
            component: PreviewPage,
            roles: ['instructor', 'ta'],
            exact: true,
        },
        {
            path: '/assignments/:id/settings',
            component: CreateFormativePage,
            roles: ['student'],
            exact: true,
        },
        {
            path: '/assignments/:id/submissions',
            component: SubmissionsPage,
            roles: ['instructor', 'ta', 'student'],
            exact: true,
        },
        {
            path: '/assignments/:id/submissions/:submissionId',
            component: ViewSubmissionPage,
            roles: ['instructor', 'ta', 'student'],
            exact: true,
        },

        { path: '/components', component: ComponentDemoPage, roles: [], exact: true },

        { path: '/course-set-up', component: CourseSetUpPage, roles: ['instructor'], exact: true },

        { path: '/lti/deep-link-picker', component: DeepLinkPickerPage, roles: [], exact: true },

        { path: '/lti-incomplete', component: AccessDenied, roles: [], exact: true },
        { path: '/session-expired', component: SessionExpiredPage, roles: [], exact: true },

        { path: '/lti-launch', component: LTILaunchPage, roles: [], exact: true },

        { path: '/people', component: People, roles: ['instructor', 'ta'], exact: true },

        { path: '/profile-setup', component: ProfileSetupPage, roles: [], exact: true },

        { path: '/quizzes', component: QuizzesPage, roles: ['instructor', 'ta'], exact: true },
        { path: '/quizzes/new', component: CreateQuizPage, roles: ['instructor'], exact: true },
        { path: '/quizzes/:id/edit', component: EditQuizPage, roles: ['instructor'], exact: true },
        { path: '/quizzes/:id', component: ViewQuizPage, roles: ['instructor', 'ta'], exact: true },

        { path: '/rubrics', component: RubricsPage, roles: [], exact: true },
        {
            path: '/rubrics/get-started',
            component: RubricsGetStartedPage,
            roles: [],
            exact: true,
        },
        {
            path: '/rubrics/new',
            component: CreateRubricPage,
            roles: [],
            exact: true,
        },
        {
            path: '/rubrics/:id',
            component: ViewRubricPage,
            roles: [],
            exact: true,
        },
        {
            path: '/rubrics/:id/edit',
            component: EditRubricPage,
            roles: [],
            exact: true,
        },

        { path: '/settings', component: SettingsPage, roles: [], exact: true },
    ];

    return (
        <Switch>
            {((auth ? routes : noAuthRoutes) || []).map((route) => {
                return (
                    <GrantPermission
                        key={route.path}
                        exact={route.exact}
                        path={route.path}
                        component={route.component}
                        roles={route.roles}
                        auth={auth}
                        user={currentUser}
                        userRole={auth?.role}
                    />
                );
            })}
            <Route component={Error404} />
        </Switch>
    );
};

export default APIRoleBasedRouting;
